import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Steps,
  Upload,
  Select,
  Spin,
  message,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  LoadingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SetupProfileApi from "../../api/auth/SetupProfileApi";
import { useDispatch, useSelector } from "react-redux";
import { userDetails } from "../../features/UserSlice";
import { firstLogin } from "../../features/FirstLoginSlice";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { setupAccount } from "../../features/SetupAccountSlice";
import { UploadProfileApi } from "../../api/UploadProfileApi";
import ImgCrop from "antd-img-crop";
import DeleteProfileApi from "../../api/DeleteProfileApi";

const { Step } = Steps;
const { Option } = Select;

function ProfileSetup() {
  const [submit, setSubmit] = useState(false);
  const [step, setStep] = useState(1);
  const [form1, setForm1] = useState();
  const [form2, setForm2] = useState();
  const [imageUrl, setImageUrl] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const userState = useSelector((state) => state.userState.value);
  const dispatch = useDispatch();
  const [firstNameForm] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  let navigate = useNavigate();

  function beforeUpload(file) {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/jpg";
    if (!isJpgOrPng) {
      message.info("You can only upload JPG/PNG file!");
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.info("Image must smaller than 2MB!");
    }
    return isJpgOrPng && isLt2M;
  }

  const beforeUpload2 = async (info) => {
    await upload(info, userState.id);
    getBase64(info, (imageUrl) => {
      setNewImageUrl(imageUrl);
    });
    return false;
  };

  function getBase64(img, callback) {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  async function upload(file, name) {
    setSubmit(true);
    const apiResponse = await UploadProfileApi(file, name);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse) {
      setImageUrl(apiResponse.url);
    }
    setSubmit(false);
  }

  async function deleteProfile() {
    await DeleteProfileApi(userState.id);
  }

  const countryCodeSelector = (
    <Form.Item name="countryCode" noStyle>
      <Select style={{ width: 70 }}>
        <Option value="94">+94</Option>
        <Option value="91">+91</Option>
      </Select>
    </Form.Item>
  );

  function showErrorMessage(message) {
    if (message === "Current password is incorrect") {
      passwordForm.setFields([
        {
          name: "currentPassword",
          errors: ["Current password is incorrect"],
        },
      ]);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't setup profile. Try again later."
      );
    }
  }

  function validatePhone(phone) {
    let validation = false;
    const pattern1 = /^\d{10}$/;
    const pattern2 = /^\d{9}$/;
    if (phone === "" || phone === undefined) {
      validation = true;
    } else if (phone.length === 10 && pattern1.test(phone)) {
      validation = true;
    } else if (
      phone.length === 9 &&
      phone.charAt(0) !== "0" &&
      pattern2.test(phone)
    ) {
      validation = true;
    }

    return validation;
  }

  function strongPassword(password) {
    let strength = false;
    const passwordArray = password.split("");
    let lowercase = false;
    let uppercase = false;
    let number = false;
    let length = false;
    // Validate lowercase letters
    const lowerCaseLetters = /[a-z]/g;
    passwordArray.forEach((pass) => {
      if (pass.match(lowerCaseLetters)) {
        lowercase = true;
      }
    });
    // Validate capital letters
    const upperCaseLetters = /[A-Z]/g;
    passwordArray.forEach((pass) => {
      if (pass.match(upperCaseLetters)) {
        uppercase = true;
      }
    });
    // Validate numbers
    const numbers = /[0-9]/g;
    passwordArray.forEach((pass) => {
      if (pass.match(numbers)) {
        number = true;
      }
    });
    // Validate length
    if (passwordArray.length >= 8) {
      length = true;
    }
    if (lowercase && uppercase && number && length) {
      strength = true;
    }

    return strength;
  }

  const onFinish1 = (values) => {
    setForm1(values);
    setStep(2);
  };

  async function onFinish2(values) {
    setForm2(values);
    validatePhone(values?.phone)
      ? setStep(3)
      : contactForm.setFields([
          {
            name: "phone",
            errors: ["Please check phone number"],
          },
        ]);
  }

  async function onFinish3(values) {
    setSubmit(true);
    let phone = form2.phone || "";
    if (phone.charAt(0) === "0") {
      phone = phone.slice(1);
    }
    let phoneNumber = phone ? form2.countryCode + phone : "";

    if (!strongPassword(values.newPassword)) {
      passwordForm.setFields([
        {
          name: "newPassword",
          errors: ["Enter a strong password!"],
        },
      ]);
    } else if (values.currentPassword === values.newPassword) {
      passwordForm.setFields([
        {
          name: "newPassword",
          errors: ["Same as current password. Please try another password!"],
        },
      ]);
    } else if (values.newPassword !== values.confirmPassword) {
      passwordForm.setFields([
        {
          name: "confirmPassword",
          errors: ["Password doesn't match!"],
        },
      ]);
    } else {
      const apiResponse = await SetupProfileApi(
        form1.firstName,
        form1.lastName || "",
        imageUrl ? imageUrl : values.image,
        phoneNumber,
        form2.address || "",
        values.currentPassword,
        values.newPassword,
        values.confirmPassword
      );
      if (apiResponse.error) {
        showErrorMessage(apiResponse.message);
      } else if (apiResponse.data?.username) {
        dispatch(firstLogin(false));
        dispatch(userDetails(apiResponse.data));
        if (!apiResponse.data?.setupAccount) {
          dispatch(setupAccount(false));
          navigate("/setup-account");
        } else {
          navigate("/home");
        }
        openNotificationSuccess("bottomRight", "Profile created successfully");
      } else {
        showErrorMessage("Something is wrong");
      }
    }
    setSubmit(false);
  }

  function renderStep1() {
    return (
      <div className=" w-full max-w-sm">
        <div className="text-xl pri-font text-center mb-6">
          Create your profile
        </div>
        <Form
          name="profileSetup1"
          className="w-full max-w-sm"
          layout={"vertical"}
          onFinish={onFinish1}
          form={firstNameForm}
          requiredMark={false}
        >
          <div className="text-center mb-2">
            <div className="flex items-center justify-center">
              <div className="w-28 h-28 bg-gray-200 rounded-full overflow-hidden justify-center flex items-center mb-3 text-gray-400 text-3xl">
                {newImageUrl || imageUrl || userState.imageUrl ? (
                  <img
                    src={
                      newImageUrl
                        ? newImageUrl
                        : imageUrl
                        ? imageUrl
                        : userState.imageUrl
                    }
                    alt="avatar"
                    className="w-28 h-28"
                  />
                ) : (
                  <UserOutlined />
                )}
              </div>
              <div className="ml-3 flex items-center gap-3">
                <ImgCrop beforeCrop={beforeUpload}>
                  <Upload
                    name="profile"
                    showUploadList={false}
                    beforeUpload={beforeUpload2}
                    // onChange={handleChange}
                  >
                    <Button>
                      {newImageUrl || imageUrl || userState.imageUrl
                        ? "Change profile"
                        : "Add profile"}
                    </Button>
                  </Upload>
                </ImgCrop>
                {newImageUrl || imageUrl || userState.imageUrl ? (
                  <div className=" text-red-500 cursor-pointer ml-3">
                    <DeleteOutlined
                      onClick={() => {
                        setImageUrl("");
                        setNewImageUrl("");
                        deleteProfile();
                      }}
                    />
                  </div>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
          <Form.Item
            label="First name"
            name="firstName"
            rules={[
              {
                required: true,
                message: "Please enter first name!",
              },
            ]}
          >
            <Input type="text" placeholder="First Name" />
          </Form.Item>
          <Form.Item label="Last name" name="lastName">
            <Input type="text" placeholder="Last Name" />
          </Form.Item>

          <Form.Item className="text-right">
            <Button type="primary" htmlType="submit" className="w-40">
              Next <ArrowRightOutlined />
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  function renderStep2() {
    return (
      <div className=" w-full max-w-sm">
        <div className="text-xl pri-font text-center mb-6">
          Add contact details
        </div>
        <Form
          name="profileSetup2"
          className="w-full max-w-sm"
          initialValues={{ countryCode: "+94" }}
          layout={"vertical"}
          onFinish={onFinish2}
          form={contactForm}
          requiredMark={false}
        >
          <Form.Item name="phone" label="Phone Number">
            <Input
              addonBefore={countryCodeSelector}
              placeholder="Phone number"
            />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea />
          </Form.Item>

          <Form.Item>
            <Button onClick={() => setStep(1)}>
              <ArrowLeftOutlined />
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="w-40 float-right"
            >
              Next <ArrowRightOutlined />
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  function renderStep3() {
    return (
      <div className=" w-full max-w-sm">
        <div className="text-xl pri-font text-center mb-6">
          Create new password
        </div>
        <Form
          name="profileSetup3"
          className="w-full max-w-sm"
          layout={"vertical"}
          onFinish={onFinish3}
          requiredMark={false}
          form={passwordForm}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[
              {
                required: true,
                message: "Please enter current password!",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#ccc" }} />}
              type="password"
              placeholder="Password"
            />
          </Form.Item>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              {
                required: true,
                message: "Please enter new password!",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#ccc" }} />}
              type="password"
              placeholder="Password"
            />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: "Please confirm your password!",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#ccc" }} />}
              type="password"
              placeholder="Password"
            />
          </Form.Item>

          <div className="mb-3 text-xs">
            Your password must be at least 8 characters long, contain at least
            one number, one uppercase letter and one lowercase letter.
          </div>

          <Form.Item>
            <Button onClick={() => setStep(2)}>
              <ArrowLeftOutlined />
            </Button>

            <Button
              type="primary"
              htmlType={submit && "submit"}
              className="w-40 float-right"
            >
              {submit ? (
                <Spin
                  size="small"
                  indicator={<LoadingOutlined style={{ color: "#fff" }} spin />}
                />
              ) : (
                <>
                  Complete <ArrowRightOutlined />
                </>
              )}
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto pt-12 pb-8">
        <div className="pri-font text-2xl text-center">
          Welcome to performance dashboard
        </div>
        <div className="text-center mb-6">
          Complete your account setup to continue
        </div>
        <div className="hidden md:block">
          <Steps current={step - 1}>
            <Step title="Step 1" description="Create profile" />
            <Step title="Step 2" description="Add contact details" />
            <Step title="Step 3" description="Create Password" />
          </Steps>
        </div>

        <div className="md:hidden">
          <Steps responsive={false} current={step}>
            <Step icon={<UserOutlined />} />
            <Step icon={<PhoneOutlined />} />
            <Step icon={<LockOutlined />} />
          </Steps>
        </div>
      </div>
      <div className="flex w-full justify-center">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </>
  );
}

export default ProfileSetup;
